import type { TransportFields, DeliveryZones } from '@/types/shipping';
import type { CommercialMedia } from '@/components/commerce/media/types';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { HOSTS } from '@/lib/hosts';
import type { OpeningHours } from '@/lib/storefrontOpeningStatus';

/**
 * Client API du storefront PUBLIC : fetch direct SANS Bearer ni X-Shop-Id
 * (contrairement à src/lib/api.ts). Seul header conditionnel : X-Page-Grant
 * pour les pages protégées déverrouillées (grant local avec expiration).
 */

const BASE = `${HOSTS.api}/api/v1/storefront`;

export interface StorefrontError extends Error {
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

const networkError = (cause: unknown): StorefrontError => {
  const error = new Error('Connexion au storefront impossible') as StorefrontError;
  if (cause instanceof Error) error.stack = cause.stack;
  error.code = 'NETWORK_ERROR';
  error.status = 0;
  return error;
};

export const isRetryableStorefrontError = (error: StorefrontError): boolean => {
  const status = error.status ?? 0;
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
};

const retryStorefrontRequest = (failureCount: number, error: StorefrontError): boolean =>
  failureCount < 2 && isRetryableStorefrontError(error);

const storefrontRetryDelay = (attemptIndex: number): number =>
  Math.min(500 * 2 ** attemptIndex, 2_000);

export const pageGrantKey = (pageId: string) => `commerce_grant_${pageId}`;

export const clearPageGrant = (pageId: string): void => {
  const key = pageGrantKey(pageId);
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

interface StoredPageGrant {
  value: string;
  expiresAt: string;
}

export const getPageGrant = (pageId: string): string | null => {
  const key = pageGrantKey(pageId);
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as StoredPageGrant;
      if (parsed.value && new Date(parsed.expiresAt).getTime() > Date.now()) return parsed.value;
    } catch {
      // Legacy/plain grants are intentionally discarded: they have no expiry.
    }
    localStorage.removeItem(key);
  }

  const legacy = sessionStorage.getItem(key);
  if (legacy) {
    sessionStorage.removeItem(key);
    return legacy;
  }

  return null;
};

async function storefrontFetch<T>(
  path: string,
  options: RequestInit = {},
  pageId?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const grant = pageId ? getPageGrant(pageId) : null;
  if (grant) headers['X-Page-Grant'] = grant;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...options,
      headers,
      credentials: 'include',
      cache: 'no-store',
    });
  } catch (error) {
    throw networkError(error);
  }
  const body = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: T;
    error?: { code?: string; message?: string; details?: Record<string, unknown> };
  } | null;

  if (!response.ok || body?.success === false) {
    const error = new Error(body?.error?.message ?? 'Erreur réseau') as StorefrontError;
    error.code = body?.error?.code;
    error.status = response.status;
    error.details = body?.error?.details;
    throw error;
  }

  return (body?.data ?? null) as T;
}

async function storefrontFetchWithMeta<T, M>(
  path: string,
  options: RequestInit = {},
  pageId?: string,
): Promise<{ data: T; meta: M }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string>),
  };
  const grant = pageId ? getPageGrant(pageId) : null;
  if (grant) headers['X-Page-Grant'] = grant;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...options,
      headers,
      credentials: 'include',
      cache: 'no-store',
    });
  } catch (error) {
    throw networkError(error);
  }
  const body = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: T;
    meta?: M;
    error?: { code?: string; message?: string; details?: Record<string, unknown> };
  } | null;

  if (!response.ok || body?.success === false) {
    const error = new Error(body?.error?.message ?? 'Erreur réseau') as StorefrontError;
    error.code = body?.error?.code;
    error.status = response.status;
    error.details = body?.error?.details;
    throw error;
  }

  return { data: (body?.data ?? []) as T, meta: body?.meta as M };
}

// ---------------------------------------------------------------------------
// Types publics (payloads whitelist du backend)
// ---------------------------------------------------------------------------

export interface PublicPagePayload {
  id: string;
  name: string;
  locked: boolean;
  access_mode: string;
  noindex: boolean;
  shareable?: boolean;
  logo_url: string | null;
  accent_color: string | null;
  has_password?: boolean;
  slug?: string | null;
  is_default?: boolean;
  mode?: 'catalog' | 'order' | 'reservation';
  contains_digital_products?: boolean;
  description?: string | null;
  media?: CommercialMedia[];
  cover_image_url?: string | null;
  layout?: string | null;
  restaurant_palette?: import('@/types/commerce').RestaurantPaletteKey | null;
  custom_restaurant_palette?: import('@/types/commerce').CustomRestaurantPalette | null;
  show_categories?: boolean;
  pagination_mode?: 'infinite' | 'classic';
  products_per_page?: number;
  category_position?: 'auto' | 'left' | 'top';
  density?: 'comfortable' | 'compact';
  image_ratio?: 'square' | 'portrait' | 'landscape';
  ip_restricted?: boolean;
  hero_style?: 'cover' | 'compact';
  product_pages_enabled?: boolean;
  product_page_layout?: import('@/types/commerce').ProductPageLayout;
  cross_sell_enabled?: boolean;
  order_document_format?: 'a4' | 'thermal';
  online_payment_mode?: 'disabled' | 'optional' | 'required';
  contact_phone?: string | null;
  contact_email?: string | null;
  whatsapp_number?: string | null;
  social_links?: Partial<
    Record<'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'x' | 'linkedin', string>
  >;
  social_links_position?: 'header' | 'footer' | 'both';
  whatsapp_checkout_enabled?: boolean;
  fulfillment_options?: 'pickup' | 'delivery' | 'both' | 'table';
  delivery_fee_mode?: 'fixed' | 'to_confirm';
  delivery_fee?: number | null;
  footer?: {
    enabled: boolean;
    tagline: string | null;
    show_contact: boolean;
    show_social_links: boolean;
    show_powered_by: boolean;
  };
  booking_schedule?: Record<string, Array<{ start: string; end: string }>>;
  booking_min_notice_minutes?: number;
  booking_horizon_days?: number;
  booking_location_mode?: 'in_store' | 'at_customer' | 'remote' | 'customer_choice';
  booking_cta_label?: 'reserve' | 'appointment' | 'request_slot';
  seo?: { title: string | null; description: string | null; og_image_url: string | null };
  shop?: {
    name: string;
    slug: string | null;
    city: string | null;
    country: string | null;
    currency: string;
    timezone: string;
  };
  redirect_to?: string;
  permanent?: boolean;
  settings?: Record<string, unknown> & { delivery_zones?: DeliveryZones | null };
  opening_hours?: OpeningHours;
  feedback?: {
    enabled: boolean;
    public_reviews_enabled: boolean;
  };
}

export interface PublicCatalogItem {
  item_id: string;
  product_id: string;
  slug?: string;
  title: string;
  description: string | null;
  media?: CommercialMedia[] | null;
  images: string[];
  price: number;
  original_price?: number | null;
  min_quantity: number;
  nature: 'good' | 'service';
  delivery_mode?: 'physical' | 'service' | 'digital';
  digital_ready?: boolean;
  digital_delivery?: { expires_in_days: number; max_downloads: number } | null;
  duration_minutes: number;
  price_display_mode: 'fixed' | 'starting_at' | 'on_request';
  available: boolean;
  availability_reason?: 'stock' | 'schedule' | 'manual' | null;
  available_weekdays?: Array<
    'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
  > | null;
  next_available_weekday?:
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
    | null;
  category: { id: string; name: string } | null;
  features: Array<{ label: string; value: string }>;
  portion_options?: Array<{ id: string; label: string; variant_ids: string[] | null }>;
  variants: Array<{
    id: string;
    name: string;
    price: number;
    original_price?: number | null;
    min_quantity: number;
    available: boolean;
    images: string[];
    attributes: Array<{ name: string; value: string }>;
  }>;
  recommendations?: PublicCatalogItem[];
}

export interface PublicCatalogMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  has_more: boolean;
  facets: {
    total: number;
    promotions?: number;
    categories: Array<{ id: string; name: string; count: number }>;
  };
}

export interface PublicCatalogResponse {
  data: PublicCatalogItem[];
  meta: PublicCatalogMeta;
}

export interface QuoteLine {
  original_unit_price?: number | null;
  product_id: string;
  product_variant_id: string | null;
  title: string;
  variant_name: string | null;
  portion_option_id: string | null;
  portion_name_snapshot: string | null;
  portion_missing: boolean;
  delivery_mode: 'physical' | 'service' | 'digital';
  unit_price: number;
  quantity: number;
  min_quantity: number;
  quantity_adjusted: boolean;
  line_total: number;
  available: boolean;
  availability_reason?: 'stock' | 'schedule' | 'manual' | null;
  available_weekdays?: PublicCatalogItem['available_weekdays'];
  next_available_weekday?: PublicCatalogItem['next_available_weekday'];
}

export interface CartQuote {
  mode: string;
  cart_mode: 'physical' | 'service' | 'digital' | 'mixed';
  lines: QuoteLine[];
  removed_lines: Array<{
    product_id: string;
    product_variant_id: string | null;
    portion_option_id: string | null;
    portion_name_snapshot: string | null;
    portion_missing: boolean;
    reason: string;
  }>;
  subtotal: number;
  discount_total: number;
  promo: { valid: boolean; reason: string | null; discount: number; code: string | null } | null;
  delivery: {
    transport_mode?: 'local_delivery' | 'shipping';
    destination_status?: 'required' | 'available' | 'unavailable';
    fulfillment: 'pickup' | 'delivery' | 'table' | 'digital' | 'mixed';
    fee: number | null;
    fee_status: 'fixed' | 'to_confirm' | null;
  };
  total: number;
}

export interface CartLineInput {
  product_id: string;
  product_variant_id?: string | null;
  portion_option_id: string | null;
  quantity: number;
}

export interface PublicDiningTable {
  id: string;
  name: string;
  zone: string | null;
  capacity: number | null;
  public_token?: string;
}

export interface TableClaim {
  claim: string;
  level: 'standard' | 'reinforced' | 'strict';
  expires_at: string;
  origin_table: PublicDiningTable;
  network_trusted: boolean;
}

export interface OrderReceipt {
  reference: string;
  status: string;
  subtotal: number;
  discount_total: number;
  delivery_fee: number | null;
  delivery_fee_status: string | null;
  total: number;
  whatsapp_url: string | null;
  tracking_url: string;
  share_card_url?: string | null;
  order_document_available: boolean;
  document_format: 'a4' | 'thermal';
  payment_status: 'unpaid' | 'pending' | 'paid' | 'failed' | 'expired' | 'manual_review';
  digital_downloads?: DigitalDownload[];
  payment?: {
    status: string;
    status_token: string;
    payment_url: string | null;
    expires_at: string | null;
  } | null;
  table_id?: string | null;
  table_name?: string | null;
  table_zone?: string | null;
  origin_table_id?: string | null;
  origin_table_name?: string | null;
  origin_table_zone?: string | null;
}

export interface DigitalDownload {
  id: string;
  name: string | null;
  product_name: string | null;
  variant_name: string | null;
  download_url: string;
  status: 'available' | 'revoked' | 'expired' | string;
  downloads_remaining: number;
  max_downloads: number;
  expires_at: string | null;
}

export interface ShopperOrderSummary {
  reference: string;
  status: string;
  total: number;
  created_at: string | null;
  tracking_url: string;
  invoice_available: boolean;
  items: Array<{
    product_id: string;
    product_variant_id: string | null;
    portion_option_id: string | null;
    portion_name_snapshot: string | null;
    portion_missing: boolean;
    name: string;
    variant_name: string | null;
    quantity: number;
  }>;
}

export interface ShopperContext {
  profile: { name: string; phone: string | null; email?: string | null; verified?: boolean } | null;
  orders: ShopperOrderSummary[];
  reservations: ShopperReservationSummary[];
  meta: { current_page: number; last_page: number; per_page: number; total: number };
  reservation_meta: { current_page: number; last_page: number; per_page: number; total: number };
}

export interface ShopperReservationSummary {
  reference: string;
  status: string;
  requested_starts_at: string;
  confirmed_starts_at: string | null;
  created_at: string | null;
  tracking_url: string;
}

export interface ReservationReceipt {
  reference: string;
  status: string;
  tracking_url: string;
  requested_starts_at: string;
  estimated_total: number;
}

export interface TrackedReservation {
  id: string;
  reference: string;
  status: string;
  customer_name: string;
  customer_phone: string;
  note: string | null;
  location: 'in_store' | 'at_customer' | 'remote';
  location_details: string | null;
  requested_starts_at: string;
  proposed_starts_at: string | null;
  confirmed_starts_at: string | null;
  timezone: string;
  change_note: string | null;
  estimated_total: string;
  sale_id: string | null;
  page_name?: string;
  page?: {
    name: string;
    logo_url: string | null;
    accent_color: string | null;
    shop_name: string | null;
    currency: string;
  };
  items: Array<{
    id: string;
    product_name: string;
    variant_name: string | null;
    unit_price: string;
    final_unit_price: string | null;
    duration_minutes: number;
    price_display_mode: 'fixed' | 'starting_at' | 'on_request';
  }>;
}

export interface TrackedOrder extends TransportFields {
  shipping_online_payment?: boolean;
  reference: string;
  status: string;
  payment_status?: string;
  preparation_status?: string;
  digital_downloads?: DigitalDownload[];
  delivery?: {
    driver: { display_name: string; phone: string | null } | null;
    assigned_at: string | null;
    out_for_delivery_at: string | null;
    delivered_at: string | null;
  } | null;
  revision: number;
  can_modify: boolean;
  modification_mode: 'direct' | 'approval' | null;
  modification_block_reason: string | null;
  cancellation_reason: string | null;
  active_change_request: {
    id: string;
    status: string;
    proposed_total: number;
    requested_at: string | null;
  } | null;
  change_history: Array<{
    id: string;
    status: 'applied' | 'accepted' | 'refused' | 'cancelled' | 'conflict';
    base_revision: number;
    previous_total: number;
    proposed_total: number;
    decision_reason: string | null;
    requested_at: string | null;
    decided_at: string | null;
  }>;
  customer_name: string;
  subtotal: number;
  discount_total: number;
  delivery_fee: number | null;
  total: number;
  document_format: 'a4' | 'thermal';
  invoice_available: boolean;
  created_at: string | null;
  fulfillment?: 'pickup' | 'delivery' | 'table' | 'digital' | string;
  table_identifier?: string | null;
  table_id?: string | null;
  table_name?: string | null;
  table_zone?: string | null;
  origin_table_id?: string | null;
  origin_table_name?: string | null;
  origin_table_zone?: string | null;
  page: {
    id: string;
    name: string;
    home_url: string;
    logo_url: string | null;
    accent_color: string | null;
    shop_name: string;
    currency: string;
    mode?: string;
    layout?: string;
    delivery_zones?: DeliveryZones | null;
    restaurant_enabled?: boolean;
    feedback_enabled?: boolean;
  } | null;
  items: Array<{
    id: string;
    product_id: string;
    product_variant_id: string | null;
    portion_option_id: string | null;
    portion_name_snapshot: string | null;
    portion_missing: boolean;
    name: string;
    variant_name: string | null;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
  order_document_url: string;
  invoice_document_url: string | null;
  whatsapp_url?: string | null;
  review_eligible?: boolean;
  existing_review?: PublicReview | null;
  share_card_url?: string | null;
}

export interface PublicReview {
  id: string;
  rating: number;
  tags: string[];
  display_name: string;
  comment: string | null;
  status: 'pending' | 'published' | 'hidden';
  is_verified: boolean;
  created_at: string | null;
}

export interface PublicReviewSummary {
  enabled: boolean;
  public_reviews_enabled: boolean;
  average_rating: number | null;
  published_count: number;
  reviews: PublicReview[];
}

export interface SubmitReviewPayload {
  rating: number;
  tags: string[];
  display_name?: string;
  comment?: string;
  cf_turnstile_response?: string;
}

export interface OrderChangeQuote {
  base_revision: number;
  mode: 'direct' | 'approval';
  subtotal: number;
  discount_total: number;
  total: number;
  promo: { valid: boolean; reason: string | null; discount: number; code: string | null } | null;
  lines: Array<{
    product_id: string;
    product_variant_id: string | null;
    portion_option_id: string | null;
    portion_name_snapshot: string | null;
    title: string;
    variant_name: string | null;
    unit_price: number;
    quantity: number;
    line_total: number;
  }>;
}

export type OrderChangeLine = {
  product_id: string;
  product_variant_id?: string | null;
  portion_option_id: string | null;
  quantity: number;
};

export const quoteOrderChange = (
  trackingToken: string,
  baseRevision: number,
  items: OrderChangeLine[],
): Promise<OrderChangeQuote> =>
  storefrontFetch(`/orders/${trackingToken}/changes/quote`, {
    method: 'POST',
    body: JSON.stringify({ base_revision: baseRevision, items }),
  });

export const submitOrderChange = (
  trackingToken: string,
  baseRevision: number,
  items: OrderChangeLine[],
): Promise<{ id: string; status: string; order_revision: number; proposed_total: number }> =>
  storefrontFetch(`/orders/${trackingToken}/changes`, {
    method: 'POST',
    body: JSON.stringify({ base_revision: baseRevision, items }),
  });

export const cancelOrderChange = (trackingToken: string, changeId: string): Promise<void> =>
  storefrontFetch(`/orders/${trackingToken}/changes/${changeId}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Hooks / appels
// ---------------------------------------------------------------------------

export interface StorePageParams {
  shopSlug?: string;
  pageSlug?: string;
  accessToken?: string;
}

export function useStorePage(params: StorePageParams, grantVersion = 0) {
  const path = params.accessToken
    ? `/t/${params.accessToken}`
    : params.pageSlug
      ? `/resolve/${params.shopSlug}/p/${params.pageSlug}`
      : `/resolve/${params.shopSlug}`;

  return useQuery<PublicPagePayload, StorefrontError>({
    queryKey: ['storefront', 'page', path, grantVersion],
    queryFn: async () => {
      // Le grant n'est connu qu'après résolution ; pour les liens à jeton on
      // tente de retrouver un grant déjà stocké via un premier passage.
      const first = await storefrontFetch<PublicPagePayload>(path);
      if (first.locked && getPageGrant(first.id)) {
        return storefrontFetch<PublicPagePayload>(path, {}, first.id);
      }
      return first;
    },
    enabled: Boolean(params.accessToken || params.shopSlug),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: retryStorefrontRequest,
    retryDelay: storefrontRetryDelay,
  });
}

export interface StoreCatalogFilters {
  q?: string;
  categoryId?: string;
  promotionsOnly?: boolean;
  page?: number;
}

function catalogPath(pageId: string | undefined, filters: StoreCatalogFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.categoryId) params.set('category_id', filters.categoryId);
  if (filters.promotionsOnly) params.set('promotions_only', '1');
  if (filters.page && filters.page > 1) params.set('page', String(filters.page));
  const query = params.toString();
  return `/pages/${pageId}/catalog${query ? `?${query}` : ''}`;
}

export function useStoreCatalog(
  pageId: string | undefined,
  filters: StoreCatalogFilters = {},
  enabled = true,
) {
  return useQuery<PublicCatalogResponse, StorefrontError>({
    queryKey: ['storefront', 'catalog', pageId, filters],
    queryFn: () =>
      storefrontFetchWithMeta<PublicCatalogItem[], PublicCatalogMeta>(
        catalogPath(pageId, filters),
        {},
        pageId,
      ),
    enabled: Boolean(pageId) && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: retryStorefrontRequest,
    retryDelay: storefrontRetryDelay,
    placeholderData: keepPreviousData,
  });
}

export function useStoreProduct(pageId: string | undefined, itemId: string | undefined) {
  return useQuery<PublicCatalogItem, StorefrontError>({
    queryKey: ['storefront', 'product', pageId, itemId],
    queryFn: () =>
      storefrontFetch<PublicCatalogItem>(`/pages/${pageId}/products/${itemId}`, {}, pageId),
    enabled: Boolean(pageId && itemId),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: retryStorefrontRequest,
    retryDelay: storefrontRetryDelay,
  });
}

export function useInfiniteStoreCatalog(
  pageId: string | undefined,
  filters: Omit<StoreCatalogFilters, 'page'> = {},
  enabled = true,
) {
  return useInfiniteQuery<PublicCatalogResponse, StorefrontError>({
    queryKey: ['storefront', 'catalog', 'infinite', pageId, filters],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      storefrontFetchWithMeta<PublicCatalogItem[], PublicCatalogMeta>(
        catalogPath(pageId, { ...filters, page: Number(pageParam) }),
        {},
        pageId,
      ),
    getNextPageParam: (lastPage) =>
      lastPage.meta.has_more ? lastPage.meta.current_page + 1 : undefined,
    enabled: Boolean(pageId) && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}

export function useShopperContext(pageId: string | undefined, page = 1) {
  return useQuery<ShopperContext, StorefrontError>({
    queryKey: ['storefront', 'shopper', pageId, page],
    queryFn: () =>
      storefrontFetch<ShopperContext>(`/pages/${pageId}/customer-context?page=${page}`, {}, pageId),
    enabled: Boolean(pageId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useTrackedOrder(trackingToken: string | undefined) {
  return useQuery<TrackedOrder, StorefrontError>({
    queryKey: ['storefront', 'tracked-order', trackingToken],
    queryFn: () => storefrontFetch<TrackedOrder>(`/orders/${trackingToken}`),
    enabled: Boolean(trackingToken),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5_000;
      if (
        data.status === 'cancelled' ||
        data.status === 'refused' ||
        data.preparation_status === 'served'
      ) {
        return false;
      }
      return 5_000;
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

export function usePublicReviews(pageId: string | undefined, enabled = true) {
  return useQuery<PublicReviewSummary, StorefrontError>({
    queryKey: ['storefront', 'reviews', pageId],
    queryFn: () => storefrontFetch<PublicReviewSummary>(`/pages/${pageId}/reviews`, {}, pageId),
    enabled: Boolean(pageId) && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

export const submitGuestReview = (
  pageId: string,
  payload: SubmitReviewPayload,
): Promise<PublicReview> =>
  storefrontFetch<PublicReview>(
    `/pages/${pageId}/reviews`,
    { method: 'POST', body: JSON.stringify(payload) },
    pageId,
  );

export const submitVerifiedReview = (
  trackingToken: string,
  payload: SubmitReviewPayload,
): Promise<PublicReview> =>
  storefrontFetch<PublicReview>(`/orders/${trackingToken}/review`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export async function fetchShareCard(url: string): Promise<Blob> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'image/png' },
  });
  if (!response.ok) throw new Error('La carte souvenir est indisponible.');
  return response.blob();
}

export function useTrackedReservation(trackingToken: string | undefined) {
  return useQuery<TrackedReservation, StorefrontError>({
    queryKey: ['storefront', 'tracked-reservation', trackingToken],
    queryFn: () => storefrontFetch<TrackedReservation>(`/reservations/${trackingToken}`),
    enabled: Boolean(trackingToken),
    refetchInterval: 30_000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

export const forgetStorefrontDevice = (): Promise<{ forgotten: boolean }> =>
  storefrontFetch<{ forgotten: boolean }>('/device', { method: 'DELETE' });

export const requestHistoryCode = (
  pageId: string,
  email: string,
  locale: 'fr' | 'en' = 'fr',
): Promise<{
  accepted: boolean;
  expires_in: number;
  resend_after: number;
  message: string;
}> =>
  storefrontFetch(
    `/pages/${pageId}/identity/code`,
    {
      method: 'POST',
      body: JSON.stringify({ email, locale }),
    },
    pageId,
  );

export const verifyHistoryCode = (
  pageId: string,
  email: string,
  code: string,
): Promise<{
  verified: boolean;
  profile: { name: string; phone: string | null; email: string };
}> =>
  storefrontFetch(
    `/pages/${pageId}/identity/verify`,
    {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    },
    pageId,
  );

export const recoverOrderByReference = (
  pageId: string,
  phone: string,
  reference: string,
): Promise<{ recovered: boolean; tracking_url: string }> =>
  storefrontFetch(
    `/pages/${pageId}/orders/recover`,
    {
      method: 'POST',
      body: JSON.stringify({ phone, reference }),
    },
    pageId,
  );

export const quoteCart = (
  pageId: string,
  items: CartLineInput[],
  fulfillment: import('@/lib/storefrontFulfillment').StorefrontFulfillment,
  promoCode?: string | null,
  destination: { destination_country?: string; destination_city?: string } = {},
): Promise<CartQuote> =>
  storefrontFetch<CartQuote>(
    `/pages/${pageId}/cart/quote`,
    {
      method: 'POST',
      body: JSON.stringify({ items, fulfillment, promo_code: promoCode ?? null, ...destination }),
    },
    pageId,
  );

export const useDiningTables = (pageId?: string) =>
  useQuery({
    queryKey: ['storefront', 'dining-tables', pageId],
    queryFn: () =>
      storefrontFetch<PublicDiningTable[]>(`/pages/${pageId ?? ''}/dining-tables`, {}, pageId),
    enabled: Boolean(pageId),
    staleTime: 30_000,
  });

export const createTableClaim = (
  pageId: string,
  qrToken: string,
  sessionCode?: string,
): Promise<TableClaim> =>
  storefrontFetch<TableClaim>(
    `/pages/${pageId}/table-claims`,
    {
      method: 'POST',
      body: JSON.stringify({ qr_token: qrToken, session_code: sessionCode || null }),
    },
    pageId,
  );

export const unlockPage = async (pageId: string, password: string): Promise<boolean> => {
  try {
    const result = await storefrontFetch<{ grant: string; expires_at: string }>(
      `/pages/${pageId}/unlock`,
      {
        method: 'POST',
        body: JSON.stringify({ password }),
      },
    );
    localStorage.setItem(
      pageGrantKey(pageId),
      JSON.stringify({
        value: result.grant,
        expiresAt: result.expires_at,
      } satisfies StoredPageGrant),
    );
    sessionStorage.removeItem(pageGrantKey(pageId));
    return true;
  } catch {
    return false;
  }
};

export interface CreateOrderPayload {
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  note?: string;
  marketing_consent: boolean;
  whatsapp_consent?: boolean;
  fulfillment: 'pickup' | 'delivery' | 'table' | 'digital';
  destination_country?: string;
  destination_city?: string;
  delivery_address?: string;
  table_id?: string;
  origin_table_id?: string;
  table_identifier?: string;
  table_claim?: string;
  presence_location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
    captured_at: number;
  };
  items: CartLineInput[];
  expected_total?: number;
  via_whatsapp?: boolean;
  promo_code?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  cf_turnstile_response?: string;
  payment_choice?: 'online' | 'offline';
}

export interface CommercePaymentStatus {
  status: 'pending' | 'paid' | 'failed' | 'expired' | 'manual_review';
  order_reference: string;
  order_status: string;
  payment_url: string | null;
  expires_at: string | null;
  paid_at: string | null;
}

export const getCommercePaymentStatus = (statusToken: string): Promise<CommercePaymentStatus> =>
  storefrontFetch<CommercePaymentStatus>(`/payments/${encodeURIComponent(statusToken)}`);

export const createPublicOrder = (
  pageId: string,
  payload: CreateOrderPayload,
  idempotencyKey: string,
): Promise<OrderReceipt> =>
  storefrontFetch<OrderReceipt>(
    `/pages/${pageId}/orders`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(payload),
    },
    pageId,
  );

export interface CreateReservationPayload {
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  note?: string;
  marketing_consent: boolean;
  requested_starts_at: string;
  location: 'in_store' | 'at_customer' | 'remote';
  location_details?: string;
  items: Array<{ product_id: string; product_variant_id?: string | null }>;
  cf_turnstile_response?: string;
}

export const createPublicReservation = (
  pageId: string,
  payload: CreateReservationPayload,
  idempotencyKey: string,
): Promise<ReservationReceipt> =>
  storefrontFetch<ReservationReceipt>(
    `/pages/${pageId}/reservations`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(payload),
    },
    pageId,
  );

export const acceptReservationProposal = (trackingToken: string): Promise<TrackedReservation> =>
  storefrontFetch<TrackedReservation>(`/reservations/${trackingToken}/accept`, { method: 'POST' });

export const cancelPublicReservation = (trackingToken: string): Promise<TrackedReservation> =>
  storefrontFetch<TrackedReservation>(`/reservations/${trackingToken}/cancel`, { method: 'POST' });

/** Analytics fire-and-forget — jamais bloquant pour l'UX. */
export const trackEvents = (
  pageId: string,
  events: Array<{ type: string; product_id?: string; utm?: Record<string, string> }>,
): void => {
  void storefrontFetch(`/pages/${pageId}/events`, {
    method: 'POST',
    body: JSON.stringify({ events }),
  }).catch(() => undefined);
};

export interface PublicShopItem {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  cover_image_url: string | null;
  description: string | null;
  category: string | null;
  category_key: string;
  sub_category: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  whatsapp_number: string | null;
  is_online: boolean;
}

export interface PublicDirectoryMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface PublicDirectoryResponse {
  cities?: string[];
  shops: PublicShopItem[];
  meta: PublicDirectoryMeta;
}

export interface DirectorySearchParams {
  query?: string;
  category?: string;
  city?: string;
  page?: number;
  per_page?: number;
}

export const fetchDirectoryShops = (
  params: DirectorySearchParams = {},
): Promise<PublicDirectoryResponse> => {
  const queryParams = new URLSearchParams();
  if (params.query) queryParams.set('query', params.query);
  if (params.category) queryParams.set('category', params.category);
  if (params.city) queryParams.set('city', params.city);
  if (params.page) queryParams.set('page', String(params.page));
  if (params.per_page) queryParams.set('per_page', String(params.per_page));

  const queryString = queryParams.toString();
  const path = `/directory/search${queryString ? `?${queryString}` : ''}`;

  return storefrontFetch<PublicDirectoryResponse>(path);
};

export const usePublicDirectoryShops = (params: DirectorySearchParams = {}) => {
  return useQuery({
    queryKey: ['storefront', 'directory', params],
    queryFn: () => fetchDirectoryShops(params),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
};

export const decideShippingQuote = (
  token: string,
  version: number,
  decision: 'accepted' | 'refused',
) =>
  storefrontFetch<TrackedOrder>(`/orders/${token}/shipping-quote/decision`, {
    method: 'POST',
    body: JSON.stringify({ version, decision }),
  });
export const payShippingQuote = (token: string) =>
  storefrontFetch<{ payment_url: string; status_token: string }>(
    `/orders/${token}/shipping-quote/payment`,
    { method: 'POST' },
  );
